import { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import "./ConditionalTooltipComponent.scss";

interface ConditionalTooltipProps {
  children: React.ReactNode;
  text: string;
  className?: string;
  disabled?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  style?: React.CSSProperties;
}

const getTrueTextWidth = (text: string, element: HTMLElement): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return 0; // Fallback
  }

  const computedStyle = window.getComputedStyle(element);
  const font = `${computedStyle.fontStyle} ${computedStyle.fontWeight} ${computedStyle.fontSize} ${computedStyle.fontFamily}`;
  context.font = font;
  
  const metrics = context.measureText(text);
  return metrics.width;
};

// Helper component for conditional tooltip
const ConditionalTooltipComponent = ({
  children,
  text,
  className = "",
  disabled = false,
  side = "top",
  style,
}: ConditionalTooltipProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    if (disabled || !textRef.current) {
      setIsOverflowing(false);
      return;
    }
    
    const element = textRef.current;
    
    // Use the high-accuracy measurement function
    const trueTextWidth = getTrueTextWidth(text, element);
    const containerWidth = element.clientWidth;

    // Check if the true width of the text is greater than the container's visible width.
    // We use a small tolerance (e.g., 1px) to account for sub-pixel rendering differences.
    const hasOverflow = trueTextWidth > containerWidth + 1;
    
    setIsOverflowing(hasOverflow);
  }, [text, disabled]);

  useEffect(() => {
    // No need to run checks if the component is disabled.
    if (disabled) {
      setIsOverflowing(false);
      return;
    }

    const element = textRef.current;
    if (!element) return;

    // Initial check when component mounts or text changes
    checkOverflow();

    // Use ResizeObserver to re-check when the element's size changes
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(element);

    // Cleanup the observer when the component unmounts or dependencies change
    return () => {
      resizeObserver.disconnect();
    };
  }, [children, text, disabled, checkOverflow]);

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <div ref={textRef} className={className} style={style}>
          {children}
        </div>
      </TooltipTrigger>
      {isOverflowing && !disabled && (
        <TooltipContent side={side}>
          {text}
        </TooltipContent>
      )}
    </Tooltip>
  );
};

export default ConditionalTooltipComponent;

